import React, { createContext, useContext, useEffect, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { getDeviceFingerprint } from '../lib/fingerprint';
import { phIdentify, phReset } from '../lib/posthog';

export interface Profile {
  id: string;
  email: string | null;
  full_name: string | null;
  birth_date: string | null;
  birth_time: string | null;
  birth_city: string | null;
  birth_country: string | null;
  birth_lat: number | null;
  birth_lng: number | null;
  sun_sign: string | null;
  moon_sign: string | null;
  gender: string | null;
  avatar_url: string | null;
  role: 'user' | 'admin';
  subscription_tier: 'free' | 'monthly' | 'annual' | 'astrologer_pro';
  is_admin: boolean;
  subscription_expires_at: string | null;
  unlimited_access: boolean;
  language: string | null;
  referral_code: string | null;
  referred_by: string | null;
  onboarding_done: boolean;
  lgpd_accepted_at: string | null;
  currency: string | null;
  banned_at: string | null;
  ban_reason: string | null;
  created_at: string;
  updated_at: string;
  // Dunning / payment status (added by dunning_system migration)
  payment_failed?: boolean | null;
  // Fiscal / NF-e fields (added by fiscal_system migration)
  cpf?: string | null;
  fiscal_address?: { street?: string; number?: string; complement?: string; neighborhood?: string; city?: string; state?: string; zip_code?: string } | null;
  fiscal_city?: string | null;
  fiscal_state?: string | null;
  fiscal_zip_code?: string | null;
}

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  profileError: boolean;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session,      setSession]      = useState<Session | null>(null);
  const [profile,      setProfile]      = useState<Profile | null>(null);
  const [loading,      setLoading]      = useState(true);
  const [profileError, setProfileError] = useState(false);

  async function fetchProfile(userId: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      setProfileError(true);
      return;
    }

    setProfileError(false);
    if (data) {
      const raw = data as Record<string, unknown>;
      const p: Profile = {
        ...data as Profile,
        unlimited_access: raw.unlimited_access === true,
        is_admin: (raw.role as string) === 'admin',
      };
      // Enforce ban: sign out immediately if account is banned
      if (p.banned_at) {
        await supabase.auth.signOut();
        setProfile(null);
        return;
      }
      setProfile(p);
      // Identify the user in PostHog
      phIdentify(p.id, {
        plan:       p.subscription_tier,
        created_at: p.created_at,
      });
    }

    // RT-19: Store SHA-256 hash of device fingerprint (never store plain-text)
    getDeviceFingerprint().then(async fp => {
      const enc = new TextEncoder();
      const buf = await crypto.subtle.digest('SHA-256', enc.encode(fp));
      const hashed = Array.from(new Uint8Array(buf))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
      supabase.from('profiles').update({ device_fingerprint: hashed }).eq('id', userId).then(() => {});
    }).catch(() => {});
  }

  async function refreshProfile() {
    if (session?.user) await fetchProfile(session.user.id);
  }

  async function signOut() {
    await supabase.auth.signOut();
    setProfile(null);
    setProfileError(false);
    phReset();
  }

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      if (s?.user) {
        fetchProfile(s.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      if (s?.user) {
        fetchProfile(s.user.id);
        if (event === 'SIGNED_IN') {
          supabase.from('profiles').update({ last_login_at: new Date().toISOString() }).eq('id', s.user.id).then(() => {});
        }
      } else {
        setProfile(null);
        setProfileError(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{
      session,
      user: session?.user ?? null,
      profile,
      loading,
      profileError,
      refreshProfile,
      signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
